<!--
	Button — primary (cut fill, ink text), ghost (hairline on paper), or dark (for
	use inside an app surface / on a dark panel).

	Renders <a> when given `href`, otherwise <button> (the buy button is a no-JS
	<form> submit — plan/07 §2). Interactive styling is scoped CSS on tokens so the
	motion timing is exact and the markup carries no arbitrary values.

	The chartreuse-on-paper question: cut-400 as a FILL under ink-900 text is ~13:1
	and is the strongest thing on the page, which is right for the one action that
	matters. Chartreuse as *text* on paper would be illegible and is banned in the
	tokens.

	Press, don't float: the old build lifted 1px on hover, which is the tell of a
	brochure button. A real control acknowledges the press instead — hover shifts
	the fill, :active seats it down 1px. Nothing moves toward the cursor.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';
	import { resolve } from '$app/paths';

	const resolvePath = resolve as (path: string) => string;

	type Variant = 'primary' | 'ghost' | 'dark';
	type Size = 'md' | 'lg';

	let {
		children,
		href,
		variant = 'primary',
		size = 'md',
		type = 'button',
		disabled = false,
		loading = false,
		class: klass = '',
		...rest
	}: {
		children: Snippet;
		href?: string;
		variant?: Variant;
		size?: Size;
		type?: 'button' | 'submit';
		disabled?: boolean;
		/** Checkout/submit in flight — disables and marks aria-busy (05 §7 "Processing…"). */
		loading?: boolean;
		class?: string;
		[key: string]: unknown;
	} = $props();
</script>

{#if href}
	<a
		href={resolvePath(href)}
		class="btn {klass}"
		data-variant={variant}
		data-size={size}
		aria-busy={loading}
		{...rest}
	>
		{@render children()}
	</a>
{:else}
	<button
		{type}
		class="btn {klass}"
		data-variant={variant}
		data-size={size}
		disabled={disabled || loading}
		aria-busy={loading}
		{...rest}
	>
		{@render children()}
	</button>
{/if}

<style>
	.btn {
		position: relative;
		display: inline-flex;
		height: 2.75rem; /* 44px minimum hit target */
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		padding-inline: 1.25rem;
		border-radius: var(--radius-md);
		font-family: var(--font-sans);
		font-size: var(--text-body);
		font-weight: 500;
		line-height: 1;
		letter-spacing: -0.01em;
		white-space: nowrap;
		cursor: pointer;
		transition:
			background-color var(--dur-fast) var(--ease-out),
			border-color var(--dur-fast) var(--ease-out),
			box-shadow var(--dur-fast) var(--ease-out),
			transform var(--dur-fast) var(--ease-out);
	}
	.btn[data-size='lg'] {
		height: 3.25rem;
		padding-inline: 1.75rem;
		font-size: 1.125rem;
	}

	/* ── Primary: the cut. One per view. ─────────────────────────────── */
	.btn[data-variant='primary'] {
		background-color: var(--color-cut-400);
		color: var(--color-ink-900);
		font-weight: 600;
		/* A tint of the accent in the shadow, so it looks lit rather than dropped. */
		box-shadow:
			inset 0 1px 0 rgba(255, 255, 255, 0.4),
			0 1px 2px rgba(20, 20, 12, 0.08),
			0 6px 16px -4px rgba(127, 174, 46, 0.4);
	}
	.btn[data-variant='primary']:hover {
		background-color: #c4f56a; /* one step brighter than cut-400 — lights up */
	}

	/* ── Ghost: paper ────────────────────────────────────────────────── */
	.btn[data-variant='ghost'] {
		background-color: var(--color-surface);
		border: 1px solid var(--color-line-strong);
		color: var(--color-ink-900);
		box-shadow: 0 1px 2px rgba(20, 20, 12, 0.04);
	}
	.btn[data-variant='ghost']:hover {
		border-color: var(--color-ink-300);
		background-color: var(--color-panel);
	}

	/* ── Dark: for app surfaces and the footer ───────────────────────── */
	.btn[data-variant='dark'] {
		background-color: var(--color-app-800);
		border: 1px solid var(--color-app-700);
		color: var(--color-bone-100);
	}
	.btn[data-variant='dark']:hover {
		background-color: var(--color-app-700);
		border-color: var(--color-app-500);
	}

	.btn:active {
		transform: translateY(1px);
	}

	.btn:disabled,
	.btn[aria-busy='true'] {
		cursor: not-allowed;
		opacity: 0.55;
		transform: none;
	}
	.btn[aria-busy='true'] {
		/* Loading is a state, not a decoration — kill the lit shadow. */
		box-shadow: none;
	}
</style>
